import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import api from "@/lib/api";

const INFO = [
  { name: "Terms & Conditions", to: "/info/terms" },
  { name: "Privacy Policy", to: "/info/privacy" },
  { name: "Shipping Policy", to: "/info/shipping" },
  { name: "Return & Refund Policy", to: "/info/refund" },
  { name: "Contact Us", to: "/info/contact" },
  { name: "FSSAI", to: "/info/fssai" },
];

// Footer contact block is fully driven by /api/config (the singleton stored
// in `site_config`). Fields the super admin has NOT filled in yet are simply
// hidden — no dead links, no placeholder text. Business address & GST are
// intentionally never displayed here (private admin-only fields).
export default function Footer() {
  const [cfg, setCfg] = useState({
    whatsapp: "",
    phone: "",
    email: "",
    business_hours: "",
    business_address: "",
    facebook_url: "",
    instagram_url: "",
  });
  const [shop, setShop] = useState([]);
  useEffect(() => {
    api.get("/config").then((r) => setCfg((c) => ({ ...c, ...r.data }))).catch(() => {});
    api.get("/categories").then((r) => setShop(r.data || [])).catch(() => {});
  }, []);

  const waNum = (cfg.whatsapp || "").replace(/[^0-9]/g, "");
  const hasAnyContact = cfg.phone || cfg.email || cfg.whatsapp || cfg.business_hours || cfg.business_address;
  const hasAnySocial = waNum || cfg.instagram_url || cfg.facebook_url;

  return (
    <footer data-testid="site-footer" className="bg-ivory-warm text-brown border-t border-brown-border mt-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-16">
        <div className="flex justify-center mb-12">
          <Logo variant="footer" size="xl" asLink={false} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 text-left">
          <div>
            <h3 className="text-[11px] tracking-[0.28em] uppercase text-brand-red font-semibold mb-5">Shop</h3>
            <ul className="space-y-3">
              {shop.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/?cat=${s.slug}`}
                    data-testid={`footer-shop-${s.slug}`}
                    className="text-brown hover:text-brand-red text-[15px] font-medium transition-colors"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] tracking-[0.28em] uppercase text-brand-red font-semibold mb-5">Important Information</h3>
            <ul className="space-y-3">
              {INFO.map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="text-brown hover:text-brand-red text-[15px] font-medium transition-colors">
                    {i.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[11px] tracking-[0.28em] uppercase text-brand-red font-semibold mb-5">Get In Touch</h3>
            {hasAnyContact ? (
              <ul className="space-y-3 text-[15px]" data-testid="footer-contact-list">
                {cfg.business_address && (
                  <li data-testid="footer-address" className="whitespace-pre-line">
                    {cfg.business_address}
                  </li>
                )}
                {cfg.phone && (
                  <li>
                    <a data-testid="footer-phone" href={`tel:${cfg.phone}`} className="hover:text-brand-red transition-colors">
                      {cfg.phone}
                    </a>
                  </li>
                )}
                {cfg.email && (
                  <li>
                    <a data-testid="footer-email" href={`mailto:${cfg.email}`} className="hover:text-brand-red transition-colors">
                      {cfg.email}
                    </a>
                  </li>
                )}
                {cfg.whatsapp && (
                  <li>
                    <a
                      data-testid="footer-whatsapp-text"
                      href={`https://wa.me/${waNum}`}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-brand-red transition-colors"
                    >
                      WhatsApp · {cfg.whatsapp}
                    </a>
                  </li>
                )}
                {cfg.business_hours && (
                  <li data-testid="footer-hours" className="text-brown-medium">
                    {cfg.business_hours}
                  </li>
                )}
              </ul>
            ) : (
              <p data-testid="footer-contact-empty" className="text-brown-medium text-sm italic">
                Contact details coming soon.
              </p>
            )}

            {hasAnySocial && (
              <div className="mt-6 flex items-center gap-4">
                {waNum && (
                  <a
                    data-testid="footer-social-whatsapp"
                    href={`https://wa.me/${waNum}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="WhatsApp"
                    className="w-10 h-10 rounded-full border border-brown-border text-brown hover:text-ivory hover:bg-brand-red hover:border-brand-red transition-colors inline-flex items-center justify-center"
                  >
                    <i className="fa-brands fa-whatsapp text-lg" />
                  </a>
                )}
                {cfg.instagram_url && (
                  <a
                    data-testid="footer-social-instagram"
                    href={cfg.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="w-10 h-10 rounded-full border border-brown-border text-brown hover:text-ivory hover:bg-brand-red hover:border-brand-red transition-colors inline-flex items-center justify-center"
                  >
                    <i className="fa-brands fa-instagram text-lg" />
                  </a>
                )}
                {cfg.facebook_url && (
                  <a
                    data-testid="footer-social-facebook"
                    href={cfg.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="w-10 h-10 rounded-full border border-brown-border text-brown hover:text-ivory hover:bg-brand-red hover:border-brand-red transition-colors inline-flex items-center justify-center"
                  >
                    <i className="fa-brands fa-facebook-f text-lg" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-brown-border py-5 text-center text-[12px] text-brown-medium">
        © {new Date().getFullYear()} Sri Durga Foods · FSSAI compliant facility
      </div>
    </footer>
  );
}
